import PrimeReact from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useEffect, useRef, useState } from "react";
import { Field, Form } from "react-final-form";
import { Tenant } from "../../interface";
import { imageUploader } from "../../../../../utils/ImageUploader";
import { useSelector, useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { AppDispatch } from "../../../../../store";
import { createTenant, retrieveTenant, updateTenant } from "../../TenantAction";
import { CategoryEnum } from "../../../Lookup/enums";
import { LOOKUPS_KEY } from "../../../../../constants/localStorageKeys";
import LinkButton from "../../../../../constants/helpers/LinkButton";
import { Divider } from "primereact/divider";
import { Checkbox } from "primereact/checkbox";
import { RadioButton } from "primereact/radiobutton";
import { useLookupOptions } from "../../../../../constants/helpers/useLookupOptions";

interface TenantFormProps {
  editable: boolean;
  submitButtonLabel: string;
  resettable?: boolean;
}
const TenantForm = (props: TenantFormProps) => {
  const {
    editable,
    submitButtonLabel,
    resettable = true,
  } = props

  // lookups
  const {
    companySizeList, industryList, interviewerAvatarList, interviewerVoiceList,
  } = useLookupOptions()

  const { tenantId } = useParams<{ tenantId: string }>()
  const { tenant } = useSelector((state: any) => state.tenant)
  const dispatch = useDispatch<AppDispatch>()

  const uploadComponent = useRef<any>();
  const backgroundUploadComponent = useRef<any>(); // Add ref for background image upload
  const [logo, setLogo] = useState<string>(tenant.logo)
  const [interviewerBackgroundImage, setInterviewerBackgroundImage] = useState<string>(tenant.interviewerBackgroundImage) // Add state for background image

  PrimeReact.ripple = true
  const toast = useRef<any>(null)

  useEffect(() => {
    if (tenantId)
      dispatch(retrieveTenant(Number(tenantId)))
  }, [])

  useEffect(() => {
    setLogo(tenant.logo)
    setInterviewerBackgroundImage(tenant.interviewerBackgroundImage) // Update background image state
  }, [tenant])

  let initialValues: Tenant = tenant && tenantId
    ? {
      id: tenant.id,
      companyName: tenant.companyName,
      companyCode: tenant.companyCode,
      description: tenant.description,
      logo,
      registrationNo: tenant.registrationNo,
      companySize: tenant.companySize,
      industry: tenant.industry,
      benefits: tenant.benefits,
      publiclyListed: tenant.publiclyListed,
      enabled: tenant.enabled,
      createdBy: tenant.createdBy,
      interviewerName: tenant.interviewerName, // Add interviewerName
      interviewerAvatar: tenant.interviewerAvatar, // Add interviewerAvatar
      interviewerBackgroundImage, // Add interviewerBackgroundImage
      interviewerVoice: tenant.interviewerVoice, // Add interviewerVoice
    } :
    {
      companyName: '',
      companyCode: '',
      description: '',
      logo: '',
      registrationNo: '',
      companySize: '',
      industry: '',
      benefits: '',
      publiclyListed: true,
      enabled: true,
      createdBy: '',
      interviewerName: 'Michael', // Add interviewerName
      interviewerAvatar: 'Michael', // Add interviewerAvatar
      interviewerBackgroundImage: '', // Add interviewerBackgroundImage
      interviewerVoice: 'Matthew', // Add interviewerVoice
    };

  const statuses = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ]

  const validate = (data: any) => {
    let errors = {} as any;

    if (!data.companyName) {
      errors.companyName = "Company Name is required.";
    }

    if (!data.companyCode) {
      errors.companyCode = "Company Code is required.";
    }
    return errors;
  };

  const onSubmit = (data: any, form: any) => {
    let payload = {} as Tenant;
    payload.id = data.id;
    payload.companyName = data.companyName;
    payload.companyCode = data.companyCode;
    payload.logo = logo;
    payload.description = data.description;
    payload.registrationNo = data.registrationNo;
    if (data.companySize === "")
      payload.companySize = "UNDEFINED";
    else
      payload.companySize = data.companySize;
    payload.industry = data.industry;
    payload.benefits = data.benefits;
    payload.publiclyListed = data.publiclyListed; // Add publiclyListed to payload
    payload.enabled = data.enabled;
    payload.interviewerName = data.interviewerName; // Add interviewerName to payload
    payload.interviewerAvatar = data.interviewerAvatar; // Add interviewerAvatar to payload
    payload.interviewerBackgroundImage = interviewerBackgroundImage; // Add interviewerBackgroundImage to payload
    payload.interviewerVoice = data.interviewerVoice; // Add interviewerVoice to payload

    console.log({ payload });

    // update or create
    if (tenantId) {
      dispatch(updateTenant(payload)).unwrap()
        .then((tenant: Tenant) => {
          // successs
          toast.current.show({
            severity: 'success',
            summary: 'Tenant Update Success',
            detail: `Tenant: ${payload.companyName} was updated successfully.`,
            life: 3000
          })
        })
        .catch((error: any) => {
          toast.current.show({
            severity: 'warn',
            summary: 'Tenant Update Error',
            detail: `Tenant: ${payload.companyName} was not updated.`,
            life: 3000
          })
        })
    } else {
      dispatch(createTenant(payload)).unwrap()
        .then((tenant: Tenant) => {
          // successs
          toast.current.show({
            severity: 'success',
            summary: 'Tenant Create Success',
            detail: `Tenant: ${payload.companyName} was created successfully.`,
            life: 3000
          })
        })
        .catch((error: any) => {
          toast.current.show({
            severity: 'warn',
            summary: 'Tenant Create Error',
            detail: `Tenant: ${payload.companyName} was not created.`,
            life: 3000
          })
        })
    }
  };

  const isFormFieldValid = (meta: any) => !!(meta.touched && meta.error);
  const getFormErrorMessage = (meta: any) => {
    return (
      isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>
    );
  };

  const customUploadEvent = async (event: any) => {
    let base64Result = await imageUploader(event)
    setLogo(base64Result.toString());
    uploadComponent.current.clear();
  }

  const customBackgroundUploadEvent = async (event: any) => { // Add handler for background image upload
    let base64Result = await imageUploader(event)
    setInterviewerBackgroundImage(base64Result.toString());
    backgroundUploadComponent.current.clear();
  }

  const handleFormReset = (form: any) => {
    form.setConfig('keepDirtyOnReinitialize', false)
    form.restart()
    form.setConfig('keepDirtyOnReinitialize', true)
  }

  return (
    <div>
      <Toast ref={toast} />
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        keepDirtyOnReinitialize
        render={({ form, handleSubmit, pristine }) => (
          <form onSubmit={handleSubmit}>
            <div className="formgrid grid align-self-center mx-2 my-3">
              <div className="col-12">
                {tenantId &&
                  <div className="field">
                    {!editable ?
                      <LinkButton className="p-button-primary" to={"/tenants/" + tenantId + "/update"} icon="eye" label="Edit" />
                      :
                      <LinkButton to={"/tenants/" + tenantId} icon="arrow-left" label="Back" />
                    }
                  </div>
                }
              </div>
              <div className="col-12">
                <Card title="Tenant Information" className="mb-3">
                  <div className="grid align-self-center">
                    <Field
                      name="companyName"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="companyNameInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Company Name:*
                          </label>
                          {editable ?
                            <InputText
                              id="companyNameInput"
                              {...input}
                              autoFocus
                              type="text"
                              className={classNames("inputfield w-full", {
                                "p-invalid": isFormFieldValid(meta),
                              })}
                            /> : <div>{tenant.companyName}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="companyCode"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="companyCodeInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Company Code:*
                          </label>
                          {editable ?
                            <InputText
                              id="companyCodeInput"
                              {...input}
                              type="text"
                              className={classNames("inputfield w-full", {
                                "p-invalid": isFormFieldValid(meta),
                              })}
                            /> : <div>{tenant.companyCode}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="description"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="descriptionInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Company Description:
                          </label>
                          {editable ?
                            <InputTextarea
                              id="descriptionInput"
                              {...input}
                              className={classNames("inputfield w-full", {
                                "p-invalid": isFormFieldValid(meta),
                              })}
                              rows={6}
                            /> : <div>{tenant.description}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <div className="field col-12 md:col-6">
                      <label htmlFor="uploadImage">Profile Picture:</label>
                      {editable &&
                        <FileUpload id="uploadImage" ref={uploadComponent}
                          className={classNames("inputfield w-full imageUploader")} customUpload auto
                          mode="basic" accept="image/*" maxFileSize={5000000} uploadHandler={customUploadEvent} />
                      }
                      <div className="mt-3">
                        {logo && logo.length > 0 &&
                          <img src={logo} alt="Display uploaded logo" style={{ width: '100px' }}></img>
                        }
                      </div>
                    </div>
                    <Field
                      name="registrationNo"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="registrationNoInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Company Registration No.:
                          </label>
                          {editable ?
                            <InputText
                              id="registrationNoInput"
                              {...input}
                              type="text"
                              className={classNames("inputfield w-full", {
                                "p-invalid": isFormFieldValid(meta),
                              })}
                            /> : <div>{tenant.registrationNo}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="companySize"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="companySizeInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Company Size:
                          </label>
                          {editable ?
                            <Dropdown
                              id="companySizePicker"
                              {...input} filter
                              className={classNames("inputfield w-full", {
                                "p-error": isFormFieldValid(meta),
                              })}
                              options={companySizeList}
                              placeholder="Select Company Size"
                            /> : <div>{tenant.companySize}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="industry"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="industryPicker"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Industry:
                          </label>
                          {editable ?
                            <Dropdown
                              id="industryPicker"
                              {...input} filter
                              className={classNames("inputfield w-full", {
                                "p-error": isFormFieldValid(meta),
                              })}
                              options={industryList}
                              placeholder="Select Industry"
                            /> : <div>{tenant.industry}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="benefits"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="benefitsInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Benefits:
                          </label>
                          {editable ?
                            <InputTextarea
                              id="benefitsInput"
                              {...input}
                              className={classNames("inputfield w-full", {
                                "p-invalid": isFormFieldValid(meta),
                              })}
                              rows={6}
                            /> : <div>{tenant.benefits}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    {/* New publiclyListed field */}
                    <Field
                      name="publiclyListed"
                      type="checkbox"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="publiclyListedInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Publicly Listed:
                          </label>
                          {editable ? (
                            <label className="block font-normal">
                              <Checkbox
                                inputId="publiclyListedInput"
                                {...input}
                                checked={input.checked || false}
                                id="publiclyListedInput"
                                className={classNames({
                                  "p-invalid": isFormFieldValid(meta),
                                  "mr-1": true,
                                })}
                              /> I allow my company to be publicly listed on X-Interviewer
                            </label>
                          ) : (
                            <div>{tenant.publiclyListed ? 'Yes' : 'No'}</div>
                          )}
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="enabled"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="enabledInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Status:
                          </label>
                          {editable ?
                            <Dropdown
                              id="statusenabledInputInput"
                              {...input}
                              options={statuses}
                              showClear
                              placeholder="Select a Status"
                              className={classNames("inputfield w-full", {
                                "p-error": isFormFieldValid(meta),
                              })}
                            /> : <div>{tenant.status ? 'Active' : 'In-active'}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />

                    <Divider />

                    <Field
                      name="interviewerName"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="interviewerNameInput"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Interviewer Name:*
                          </label>
                          {editable ?
                            <InputText
                              id="interviewerNameInput"
                              {...input}
                              type="text"
                              required={true}
                              className={classNames("inputfield w-full", {
                                "p-invalid": isFormFieldValid(meta),
                              })}
                            /> : <div>{tenant.interviewerName}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="interviewerVoice"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="interviewerVoicePicker"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Interviewer Voice:*
                          </label>
                          {editable ?
                            <Dropdown
                              id="interviewerVoicePicker"
                              {...input} filter
                              required={true}
                              className={classNames("inputfield w-full", {
                                "p-error": isFormFieldValid(meta),
                              })}
                              options={interviewerVoiceList}
                              placeholder="Select Interviewer Voice"
                            /> : <div>{tenant.interviewerVoice}</div>
                          }
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <Field
                      name="interviewerAvatar"
                      render={({ input, meta }) => (
                        <div className="field col-12 md:col-6">
                          <label
                            htmlFor="interviewerAvatarCheckbox"
                            className={classNames({
                              "p-error": isFormFieldValid(meta),
                            })}
                          >
                            Interviewer Avatar:*
                          </label>
                          {editable ? (
                            <div id="interviewerAvatarCheckbox" className="flex flex-wrap gap-4">
                              {interviewerAvatarList.map((avatar: string) => {
                                const imageUrl = `https://xi-docs.s3.ap-southeast-1.amazonaws.com/${avatar.toLowerCase()}.jpg`;
                                return (
                                  <label 
                                    key={avatar} 
                                    htmlFor={`interviewerAvatarCheckbox_${avatar}`} 
                                    className="flex flex-column align-items-center cursor-pointer">
                                    <img
                                      src={imageUrl}
                                      alt={avatar}
                                      className="interviewer-avatar mb-2"
                                    />
                                    <div className="flex align-items-center">
                                      <RadioButton 
                                        inputId={`interviewerAvatarCheckbox_${avatar}`}
                                        name={input.name}
                                        value={avatar}
                                        checked={input.value === avatar}
                                        onChange={() => input.onChange(avatar)}
                                        className="mr-1"
                                      />
                                      {avatar}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex flex-column text-center" style={{width: '120px'}}>
                              {tenant.interviewerAvatar && (
                                <img
                                  src={`https://xi-docs.s3.ap-southeast-1.amazonaws.com/${tenant.interviewerAvatar.toLowerCase()}.jpg`}
                                  alt={tenant.interviewerAvatar}
                                  className="interviewer-avatar inline-block"
                                />
                              )}
                              <div>{tenant.interviewerAvatar || '-'}</div>
                            </div>
                          )}
                          {getFormErrorMessage(meta)}
                        </div>
                      )}
                    />
                    <div className="field col-12 md:col-6">
                      <label htmlFor="uploadBackgroundImage">Interviewer Background Image:</label>
                      {editable &&
                        <FileUpload id="uploadBackgroundImage" ref={backgroundUploadComponent}
                          className={classNames("inputfield w-full imageUploader")} customUpload auto
                          mode="basic" accept="image/*" maxFileSize={5000000} uploadHandler={customBackgroundUploadEvent} />
                      }
                      <div className="mt-3">
                        {interviewerBackgroundImage && interviewerBackgroundImage.length > 0 &&
                          <img src={interviewerBackgroundImage} alt="Display uploaded background" style={{ width: '100px' }}></img>
                        }
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
              <div className="col-12">
                {editable &&
                  <div className="flex justify-content-end align-items-end">
                    {resettable ?
                      <Button
                        className="p-button-outlined p-button-secondary mr-3"
                        label="Reset"
                        icon="pi pi-refresh"
                        onClick={(e) => { handleFormReset(form); e.preventDefault() }}
                        disabled={pristine}></Button>
                      : false
                    }
                    <Button label={submitButtonLabel} icon="pi pi-check"></Button>
                  </div>
                }
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default TenantForm;